import { Flex, Input, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AvatarWithInitials,
  DateTimeCell,
  ProTableContainer
} from 'src/components'
import { DEFAULT_PER_PAGE } from 'src/hooks'

import type { RoleMemberSource } from '../WorkspaceRolesPage/types'

const { Title, Text } = Typography

type RoleMember = {
  id: string
  name: string
  email: string
  team: string
  assignedAt: string | null
  source: RoleMemberSource
  sourceGroupName: string | null
}

type RoleMembersTableProps = {
  members: RoleMember[]
}

export const RoleMembersTable = ({ members }: RoleMembersTableProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return members
    const q = search.toLowerCase()

    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    )
  }, [members, search])

  const columns: ColumnsType<RoleMember> = [
    {
      title: t('workspace.roleDetail.memberColumns.name'),
      key: 'name',
      fixed: 'left',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <AvatarWithInitials name={record.name} size={28} />
          <Text>{record.name}</Text>
        </Flex>
      )
    },
    {
      title: t('workspace.roleDetail.memberColumns.email'),
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: t('workspace.roleDetail.memberColumns.team'),
      dataIndex: 'team',
      key: 'team'
    },
    {
      title: t('workspace.roleDetail.memberColumns.source'),
      key: 'source',
      width: 190,
      render: (_, record) =>
        record.source === 'group' ? (
          <Tooltip
            title={
              record.sourceGroupName
                ? t('workspace.roleDetail.source.viaGroupHint', {
                    group: record.sourceGroupName
                  })
                : t('workspace.roleDetail.source.viaGroupHintUnknown')
            }
          >
            <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
              {record.sourceGroupName ?? t('workspace.roleDetail.source.group')}
            </Tag>
          </Tooltip>
        ) : (
          <Tag style={{ marginInlineEnd: 0 }}>
            {t('workspace.roleDetail.source.personal')}
          </Tag>
        )
    },
    {
      title: t('workspace.roleDetail.memberColumns.assigned'),
      key: 'assignedAt',
      render: (_, record) => (
        <DateTimeCell value={record.assignedAt} variant="date" />
      )
    }
  ]

  return (
    <Flex vertical gap={12}>
      <Title level={5} style={{ margin: 0 }}>
        {t('workspace.roleDetail.membersWithRole')}
      </Title>
      <Input
        placeholder={t('workspace.roleDetail.searchMembers')}
        prefix={<Search size={16} />}
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 320 }}
      />
      <ProTableContainer<RoleMember>
        showSearch={false}
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: DEFAULT_PER_PAGE }}
      />
    </Flex>
  )
}
